import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as Highcharts from 'highcharts';
import { EMPTY, Subject, Subscription } from 'rxjs';
import { catchError, switchMap, tap, takeUntil } from 'rxjs/operators';
import { AnalyticsChoice, AnalyticsFilters, AnalyticsQuery, AnalyticsReport, AnalyticsSummary, SmartSiteAnalyticsService } from 'src/app/services/smart-site-analytics.service';

@Component({selector: 'app-analytics', templateUrl: './analytics.component.html', styleUrls: ['./analytics.component.css']})
export class AnalyticsComponent implements OnInit, OnDestroy {
  Highcharts = Highcharts;
  filters: AnalyticsFilters;
  summary: AnalyticsSummary | null = null;
  report: AnalyticsReport | null = null;
  organizationId = '';
  storeId = '';
  dateFrom = this.dateString(new Date(new Date().setDate(new Date().getDate() - 29)));
  dateTo = this.dateString(new Date());
  draftDateFrom = this.dateFrom;
  draftDateTo = this.dateTo;
  interval: AnalyticsQuery['interval'] = 'daily';
  mode: 'summary' | 'items' | 'stores' = 'summary';
  page = 1;
  loading = true;
  updating = false;
  error = '';
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  trendOptions: Highcharts.Options = {};
  visitorOptions: Highcharts.Options = {};
  readonly intervals: AnalyticsQuery['interval'][] = ['daily', 'weekly', 'monthly'];
  readonly cards = [
    {key: 'qrScans', title: 'QR Scans', icon: 'qr_code_2', color: 'purple'},
    {key: 'siteViews', title: 'Smart Site Views', icon: 'language', color: 'blue'},
    {key: 'itemViews', title: 'Item Views', icon: 'sell', color: 'amber'},
  ];
  private requests = new Subject<void>();
  private cancel = new Subject<void>();
  private subscription = new Subscription();
  private filterSubscription?: Subscription;
  private filtersLoaded = false;
  private dateRangeError = false;

  constructor(private api: SmartSiteAnalyticsService, private route: ActivatedRoute, private router: Router) {}

  ngOnInit(): void {
    const params = this.route.snapshot.queryParams;
    this.organizationId = params.organizationId || '';
    this.storeId = params.storeId || '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(params.dateFrom || '')) this.dateFrom = params.dateFrom;
    if (/^\d{4}-\d{2}-\d{2}$/.test(params.dateTo || '')) this.dateTo = params.dateTo;
    this.draftDateFrom = this.dateFrom;
    this.draftDateTo = this.dateTo;
    if (this.intervals.includes(params.interval)) this.interval = params.interval;
    if (['items', 'stores'].includes(params.report)) this.mode = params.report;
    this.page = Math.max(1, Math.floor(Number(params.page) || 1));
    this.subscription.add(this.requests.pipe(
      tap(() => {this.loading = true; this.updating = false; this.summary = null; this.report = null; this.error = ''; this.persistFilters();}),
      switchMap(() => this.loadResult().pipe(
        takeUntil(this.cancel),
        catchError(error => {this.loading = false; this.updating = false; this.summary = null; this.report = null; this.error = error.error?.error?.message || 'Could not load analytics. Please try again.'; return EMPTY;}),
      )),
    ).subscribe(({data: result, cached}) => {
      this.loading = false;
      this.updating = cached;
      if ('kpis' in result) {this.summary = result; this.configureCharts();}
      else this.report = result;
    }));
    this.loadFilters();
  }

  get query(): AnalyticsQuery {
    return {organizationId: this.organizationId, ...(this.storeId ? {storeId: this.storeId} : {}),
      dateFrom: this.dateFrom, dateTo: this.dateTo, interval: this.interval, page: this.page, pageSize: 25};
  }
  private loadResult() {
    const query = this.mode === 'summary' ? {...this.query, interval: 'daily' as const, page: 1} : this.query;
    return this.api.results(this.mode, query, this.filters);
  }
  get selectedStoreName() {return this.filters?.stores.find(s => s.id === this.storeId)?.name || 'All Stores';}
  get lastPage() {return Math.max(1, Math.ceil((this.report?.total || 0) / 25));}
  get reportStart() {return this.report?.rows.length ? (this.page - 1) * 25 + 1 : 0;}
  get reportEnd() {return Math.min(this.page * 25, this.report?.total || 0);}
  get generatedAt() {return (this.mode === 'summary' ? this.summary : this.report)?.generatedAt;}

  applyDateRange(): void {
    if (!this.draftDateFrom || !this.draftDateTo || this.draftDateFrom > this.draftDateTo) {
      this.dateRangeError = true;
      this.error = 'Choose a valid start and end date.';
      return;
    }
    this.dateFrom = this.draftDateFrom;
    this.dateTo = this.draftDateTo;
    this.refresh();
  }
  retry(): void {if (this.dateRangeError) this.applyDateRange(); else if (this.filters) this.refresh(false); else this.loadFilters();}

  trackOrganization(_index: number, organization: AnalyticsChoice): string {return organization.id;}

  loadFilters(): void {
    this.cancel.next();
    this.filterSubscription?.unsubscribe();
    this.filtersLoaded = false;
    this.updating = false;
    this.summary = null; this.report = null;
    this.loading = true;
    this.error = '';
    this.filterSubscription = this.api.filters(this.organizationId || undefined).subscribe({next: filters => {
      this.filters = filters;
      this.filtersLoaded = true;
      if (!filters.enabled) {this.loading = false; return;}
      this.organizationId = filters.organizationId || '';
      if (!filters.stores.some(s => s.id === this.storeId)) this.storeId = '';
      if (this.storeId && this.mode === 'stores') this.mode = 'summary';
      if (!this.organizationId) {this.loading = false; return;}
      this.refresh(false);
    }, error: error => {this.loading = false; this.error = error.error?.error?.message || 'Could not load your organizations and stores.';}});
  }
  organizationChanged(): void {this.storeId = ''; this.page = 1; this.summary = null; this.report = null; this.loadFilters();}
  refresh(resetPage = true): void {
    this.dateRangeError = false;
    this.cancel.next();
    // A failed organization lookup must be retried before using its store choices.
    if (!this.filtersLoaded) {this.loadFilters(); return;}
    if (!this.filters.enabled || !this.organizationId) {this.loading = false; return;}
    if (!this.dateFrom || !this.dateTo || this.dateFrom > this.dateTo) {this.error = 'Choose a valid start and end date.'; this.loading = false; return;}
    if (resetPage) this.page = 1;
    if (this.storeId && this.mode === 'stores') this.mode = 'summary';
    this.requests.next();
  }
  showReport(mode: 'summary' | 'items' | 'stores'): void {this.mode = mode; this.page = 1; this.refresh();}
  setInterval(interval: AnalyticsQuery['interval']): void {
    if (this.interval === interval) return;
    this.interval = interval;
    this.persistFilters();
    this.configureCharts();
  }
  changePage(delta: number): void {this.page = Math.max(1, Math.min(this.lastPage, this.page + delta)); this.refresh(false);}
  comparison(metric: string): string {
    const change = this.summary?.comparisons[metric];
    if (change?.percent == null) return '';
    const sign = change.percent > 0 ? '+' : '';
    return `${sign}${change.percent}%`;
  }
  comparisonTitle(metric: string): string {
    return this.summary?.comparisons[metric]?.reason === 'before_tracking'
      ? 'The previous period starts before tracking began.' : 'Compared with the preceding period of the same length. No percentage is shown when its total is zero.';
  }
  imageFailed(event: Event): void {(event.target as HTMLImageElement).style.display = 'none';}

  private persistFilters(): void {
    void this.router.navigate([], {relativeTo: this.route, replaceUrl: true, queryParams: {
      ...this.query, pageSize: null, report: this.mode === 'summary' ? null : this.mode, storeId: this.storeId || null,
    }});
  }
  private configureCharts(): void {
    if (!this.summary) return;
    const trend = this.groupTrend();
    const common: Highcharts.Options = {credits: {enabled: false}, exporting: {enabled: false}, title: {text: undefined}, legend: {enabled: false}, accessibility: {enabled: false}};
    this.trendOptions = {...common, chart: {type: 'area', height: 270, backgroundColor: 'transparent', style: {fontFamily: 'inherit'}},
      xAxis: {categories: trend.map(p => p.date), tickInterval: Math.max(1, Math.ceil(trend.length / 10)), lineColor: '#e5eaf2', labels: {style: {fontSize: '11px'}}},
      yAxis: {title: {text: undefined}, min: 0, allowDecimals: false, gridLineColor: '#eef1f6'},
      tooltip: {pointFormat: '<b>{point.y:,.0f}</b> Smart Site views'},
      plotOptions: {area: {lineWidth: 2, marker: {enabled: trend.length <= 40, radius: 3}, fillColor: {linearGradient: {x1: 0, y1: 0, x2: 0, y2: 1}, stops: [[0, 'rgba(0,119,255,.22)'], [1, 'rgba(0,119,255,.02)']]}}},
      series: [{type: 'area', name: 'Smart Site views', color: '#087df1', data: trend.map(p => p.views)}]};
    this.visitorOptions = {...common, chart: {type: 'pie', height: 150, width: 150, spacing: [0, 0, 0, 0], backgroundColor: 'transparent'},
      tooltip: {pointFormat: '<b>{point.y:,.0f}</b> visits'},
      plotOptions: {pie: {innerSize: '74%', borderWidth: 0, dataLabels: {enabled: false}, states: {inactive: {opacity: 1}}}},
      series: [{type: 'pie', name: 'Visits', data: this.summary.visitors.total ? [
        {name: 'New', y: this.summary.visitors.new, color: '#087df1'}, {name: 'Returning', y: this.summary.visitors.returning, color: '#8429ed'},
      ] : [{name: 'No visits', y: 1, color: '#edf0f6'}]}]};
  }

  private groupTrend(): AnalyticsSummary['trend'] {
    const grouped = new Map<string, number>();
    for (const point of this.summary?.trend || []) {
      let day = point.date;
      if (this.interval === 'monthly') day = day.slice(0, 7) + '-01';
      if (this.interval === 'weekly') {
        // API dates already represent calendar days in the reporting timezone.
        // UTC arithmetic groups these labels without introducing browser DST shifts.
        const monday = new Date(day + 'T00:00:00Z');
        monday.setUTCDate(monday.getUTCDate() - (monday.getUTCDay() + 6) % 7);
        day = monday.toISOString().slice(0, 10);
      }
      grouped.set(day, (grouped.get(day) || 0) + point.views);
    }
    return Array.from(grouped, ([date, views]) => ({date, views}));
  }
  private dateString(date: Date) {return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;}
  ngOnDestroy(): void {this.cancel.next(); this.cancel.complete(); this.subscription.unsubscribe(); this.filterSubscription?.unsubscribe(); this.requests.complete();}
}
